import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LinkedInWizardStep1 } from "@/components/LinkedInWizard/LinkedInWizardStep1";
import { LinkedInWizardStep2 } from "@/components/LinkedInWizard/LinkedInWizardStep2";
import { LinkedInWizardStep3 } from "@/components/LinkedInWizard/LinkedInWizardStep3";
import { LinkedInWizardStep4 } from "@/components/LinkedInWizard/LinkedInWizardStep4";
import { LinkedInWizardStep5 } from "@/components/LinkedInWizard/LinkedInWizardStep5";
import { LinkedInWizardStep6 } from "@/components/LinkedInWizard/LinkedInWizardStep6";
import { LinkedInWizardStep7 } from "@/components/LinkedInWizard/LinkedInWizardStep7";
import { LinkedInWizardStep8 } from "@/components/LinkedInWizard/LinkedInWizardStep8";
import { LinkedInWizardStep9 } from "@/components/LinkedInWizard/LinkedInWizardStep9";
import { LinkedInWizardStep10 } from "@/components/LinkedInWizard/LinkedInWizardStep10";
import { LinkedInWizardStep11 } from "@/components/LinkedInWizard/LinkedInWizardStep11";
import { LinkedInWizardResults } from "@/components/LinkedInWizard/LinkedInWizardResults";
import { LinkedInWizardRecap } from "@/components/LinkedInWizard/LinkedInWizardRecap";
import LinkedInWizardContentReview from "@/components/LinkedInWizard/LinkedInWizardContentReview";
import LinkedInProfessionalReport from "@/components/LinkedInWizard/LinkedInProfessionalReport";
import { LinkedInProfileService } from "@/services/linkedinProfileService";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, User, Target, Award, MessageSquare, Building, FileText, Trophy, BarChart, Volume2, Hash, PhoneCall, CheckCircle2, Circle, Eye, Coins } from "lucide-react";
import { useNavigate } from "react-router-dom";
import linkedInLogo from "@/assets/logos/linkedin-logo.png";
import { supabase } from "@/integrations/supabase/client";
import { useCredits } from "@/hooks/useCredits";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { generateLinkedInWordDocument, WordDocumentData } from "@/utils/linkedinDocumentUtils";

const LINKEDIN_WIZARD_CREDIT_COST = 5;

export interface LinkedInWizardData {
  /** Client's LinkedIn profile URL, collected at intake for the Profile Package. */
  linkedinUrl?: string;
  /** Client headshot public URL (uploaded-documents bucket), for the Package cover. */
  photoUrl?: string;
  userCategories: string[];
  primaryGoals: string[];
  uniqueSellingPoints: string[];
  customUSP?: string;
  targetAudience: string;
  whatYouHelp: string;
  howYouHelp: string;
  valueProposition: string;
  industries: string[];
  audienceSeniority: string;
  companySize: string;
  uploadedFiles: Array<{
    file: File;
    extractedText: string;
    processingStatus: 'pending' | 'processing' | 'completed' | 'error';
    processingProgress?: number;
  }>;
  pastedText?: string;
  manuallyEnteredText?: string;
  skipExperienceHighlights?: boolean;
  experienceHighlights: {
    role: string;
    achievements: string;
  }[];
  skipResultsAndProof?: boolean;
  resultsAndProof: string;
  generatedHeadlines?: Array<{
    headline: string;
    length: number;
    approach: string;
    isValid: boolean;
  }>;
  headlineRecommendation?: {
    recommendedIndex: number;
    score: number;
    reasons: string[];
    explanation: string;
  };
  voiceStyle: string;
  includeFaith: string;
  keywords: string;
  targetJobTitles: string;
  pastJobTitles: string;
  skills: string;
  callToAction: string;
}
const TOTAL_STEPS = 14; // Added content review and results steps
const stepTitles = ["Your Role", "Primary Goals", "Unique Selling Points", "Value Proposition", "Industries & Audience", "Documents & Information", "Experience Highlights", "Results & Proof", "Voice & Style", "Keywords & Skills", "Call to Action", "Review & Confirm", "Content Review", "Results" // New steps
];
const stepIcons = [User, Target, Award, MessageSquare, Building, FileText, Trophy, BarChart, Volume2, Hash, PhoneCall, CheckCircle2, Eye, CheckCircle2];
const stepShortNames = ["Role", "Goals", "USPs", "Value", "Audience", "Docs", "Experience", "Results", "Voice", "Keywords", "CTA", "Review", "Content", "Complete"];
export default function LinkedInWizard() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const { balance, deductCredits, isLoading: creditsLoading } = useCredits();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Add scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [currentStep, setCurrentStep] = useState(1); // Start at step 1
  const [showResults, setShowResults] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<any>(null);
  const [rawContentSaved, setRawContentSaved] = useState(false);
  const [showProfessionalReport, setShowProfessionalReport] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [wordDocument, setWordDocument] = useState<WordDocumentData | null>(null);
  const [wizardData, setWizardData] = useState<LinkedInWizardData>({
    userCategories: [],
    primaryGoals: [],
    uniqueSellingPoints: [],
    targetAudience: "",
    whatYouHelp: "",
    howYouHelp: "",
    valueProposition: "",
    industries: [],
    audienceSeniority: "",
    companySize: "",
    uploadedFiles: [],
    experienceHighlights: [],
    resultsAndProof: "",
    voiceStyle: "",
    includeFaith: "",
    keywords: "",
    targetJobTitles: "",
    pastJobTitles: "",
    skills: "",
    callToAction: ""
  });

  // Load existing data when component mounts
  useEffect(() => {
    const loadExistingData = async () => {
      if (!user) return;
      const searchParams = new URLSearchParams(window.location.search);
      const profileId = searchParams.get('profileId');
      if (!profileId) return;
      try {
        const existingProfile = await LinkedInProfileService.loadProfile(profileId);
        if (existingProfile) {
          const transformedData = LinkedInProfileService.transformToWizardData(existingProfile);
          setWizardData(transformedData);
          setProfileId(existingProfile.id || null);
          toast.success("Profile data loaded");
        }
      } catch (error) {
        console.error("Error loading existing data:", error);
        // Don't show error toast - it's okay if no previous data exists
      }
    };
    loadExistingData();
  }, [user]);
  const saveData = async (dataToSave: LinkedInWizardData) => {
    if (!user || isSaving) return;
    setIsSaving(true);
    try {
      const savedProfile = await LinkedInProfileService.saveProfile(dataToSave, profileId || undefined);
      if (savedProfile && !profileId) {
        setProfileId(savedProfile.id || null);
      }
    } catch (error) {
      console.error("Error saving profile data:", error);
      // Don't show error toast for auto-save failures
    } finally {
      setIsSaving(false);
    }
  };
  const updateWizardData = (updates: Partial<LinkedInWizardData>) => {
    const newData = {
      ...wizardData,
      ...updates
    };
    setWizardData(newData);

    // Auto-save data after a short delay
    setTimeout(() => {
      saveData(newData);
    }, 1000);
  };
  const nextStep = () => {
    if (currentStep < 12) {
      setCurrentStep(currentStep + 1);
      // Save data when progressing to next step
      saveData(wizardData);
    } else if (currentStep === 12) {
      // Move to content review
      setCurrentStep(13);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const goToStep = (step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      setCurrentStep(step);
    }
  };
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return wizardData.userCategories.length > 0;
      case 2:
        return wizardData.primaryGoals.length > 0;
      case 3:
        return wizardData.uniqueSellingPoints.length > 0 || !!wizardData.customUSP;
      case 4:
        // Require all value proposition fields AND a selected headline
        const hasValuePropFields = !!wizardData.whatYouHelp && !!wizardData.howYouHelp && !!wizardData.targetAudience;
        const hasSelectedHeadline = !!wizardData.valueProposition;
        return hasValuePropFields && hasSelectedHeadline;
      case 5:
        return !!wizardData.audienceSeniority && !!wizardData.companySize;
      case 7:
        return wizardData.skipExperienceHighlights || wizardData.experienceHighlights.length > 0;
      case 8:
        return wizardData.skipResultsAndProof || !!wizardData.resultsAndProof;
      case 9:
        return !!wizardData.voiceStyle && !!wizardData.includeFaith;
      case 10:
        return !!wizardData.keywords;
      case 11:
        return !!wizardData.callToAction && wizardData.callToAction !== 'not-sure';
      default:
        return true;
    }
  };
  const handleNext = () => {
    if (!validateCurrentStep()) {
      // Custom error messages for specific steps
      if (currentStep === 4) {
        if (!wizardData.whatYouHelp || !wizardData.howYouHelp || !wizardData.targetAudience) {
          toast.error("Please fill in all three value proposition fields before generating headlines.");
        } else if (!wizardData.valueProposition) {
          toast.error("Please generate and select a LinkedIn headline before proceeding.");
        }
        return;
      }
      if (currentStep === 11) {
        toast.error("Please select a call to action before proceeding.");
        return;
      }
      toast.error("Please fill in all required fields before proceeding.");
      return;
    }
    nextStep();
  };
  const handleSaveRawContent = async (rawContentData: any) => {
    try {
      const savedProfile = await LinkedInProfileService.saveProfileWithRawContent(wizardData, rawContentData, profileId || undefined);
      if (savedProfile?.id) {
        setProfileId(savedProfile.id);
        setRawContentSaved(true);
        toast.success("Raw content saved successfully to your LinkedIn library!");
      }
    } catch (error: any) {
      console.error('Error saving raw content:', error);
      // If user is not authenticated, allow them to proceed without saving
      if (error.message === 'User not authenticated') {
        setRawContentSaved(true); // Allow proceeding without save
        toast.info("Content ready for generation. Sign in to save to your library.");
      } else {
        toast.error("Failed to save raw content. Please try again.");
      }
    }
  };

  const handleContentReviewNext = () => {
    if (!rawContentSaved) {
      toast.error("Please save the raw content before proceeding.");
      return;
    }
    
    // Check credits before generating
    if (user && balance < LINKEDIN_WIZARD_CREDIT_COST) {
      setShowUpgradePrompt(true);
      return;
    }
    
    generateProfile();
  };

  const generateProfile = async () => {
    // Generate LinkedIn profile using the comprehensive AI prompt
    try {
      setIsGenerating(true);
      
      console.log('Generating LinkedIn profile with comprehensive AI prompt:', {
        hasWizardData: Object.keys(wizardData).length > 0,
        uploadedFilesCount: wizardData.uploadedFiles?.length || 0
      });

      // Combine all content sources into resume text
      const resumeText = [
        ...(wizardData.uploadedFiles?.map(f => f.extractedText).filter(Boolean) || []),
        wizardData.pastedText,
        wizardData.manuallyEnteredText
      ].filter(Boolean).join('\n\n---\n\n');

      if (!resumeText || resumeText.trim().length < 50) {
        throw new Error('Please provide resume content through file upload, paste, or manual entry before generating.');
      }

      // Call the edge function with all wizard data
      const { data: functionData, error: functionError } = await supabase.functions.invoke('generate-linkedin-profile', {
        body: {
          resumeText,
          pastedText: wizardData.pastedText,
          manuallyEnteredText: wizardData.manuallyEnteredText,
          userCategories: wizardData.userCategories,
          primaryGoals: wizardData.primaryGoals,
          uniqueSellingPoints: wizardData.uniqueSellingPoints,
          customUSP: wizardData.customUSP,
          targetAudience: wizardData.targetAudience,
          whatYouHelp: wizardData.whatYouHelp,
          howYouHelp: wizardData.howYouHelp,
          valueProposition: wizardData.valueProposition,
          industries: wizardData.industries,
          audienceSeniority: wizardData.audienceSeniority,
          companySize: wizardData.companySize,
          experienceHighlights: wizardData.experienceHighlights,
          resultsAndProof: wizardData.resultsAndProof,
          voiceStyle: wizardData.voiceStyle,
          includeFaith: wizardData.includeFaith,
          keywords: wizardData.keywords,
          targetJobTitles: wizardData.targetJobTitles,
          pastJobTitles: wizardData.pastJobTitles,
          skills: wizardData.skills,
          callToAction: wizardData.callToAction
        }
      });

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw new Error(functionError.message || 'Failed to generate LinkedIn profile');
      }

      if (!functionData?.success) {
        throw new Error(functionData?.error || 'LinkedIn profile generation failed');
      }

      // Prefer Claude's structured `profile` (LinkedIn Magic engine); fall back
      // to wizard data + markdown blob for any legacy response shape.
      const p = functionData.profile || {};
      const result = {
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        headline: p.headline || wizardData.valueProposition || '',
        headlineAlt: p.headlineAlt || '',
        about: p.about || '',
        summary: functionData.generatedContent,
        experience: Array.isArray(p.experience) && p.experience.length
          ? p.experience
          : (wizardData.experienceHighlights || []),
        skills: Array.isArray(p.skills) && p.skills.length
          ? p.skills
          : (typeof wizardData.skills === 'string'
              ? wizardData.skills.split(/[,|;]/).map(s => s.trim()).filter(Boolean)
              : (wizardData.skills || [])),
        featured: Array.isArray(p.featured) ? p.featured : [],
        recommendations: Array.isArray(p.recommendations) ? p.recommendations : [],
        hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
        visualBranding: p.visualBranding || '',
        activityStrategy: Array.isArray(p.activityStrategy) ? p.activityStrategy : [],
        keywords: wizardData.keywords || [],
        optimizationTips: Array.isArray(p.activityStrategy) && p.activityStrategy.length
          ? p.activityStrategy
          : [
              'Use a professional headshot as your profile photo',
              'Add industry-relevant skills to your skills section',
              'Request recommendations from colleagues and clients',
              'Post regular content to increase visibility',
              'Engage with your network through comments and shares',
            ],
        rawContent: functionData.generatedContent,
        metadata: {
          generatedAt: new Date().toISOString(),
          source: 'claude_linkedin_optimizer',
          tokensUsed: functionData.usage?.input_tokens && functionData.usage?.output_tokens
            ? functionData.usage.input_tokens + functionData.usage.output_tokens
            : functionData.usage?.total_tokens,
        },
      };

      setGeneratedProfile(result);

      // Generate Word document via Docmosis (non-blocking)
      try {
        const targetTitle = wizardData.targetJobTitles || wizardData.valueProposition || '';
        const wordDoc = await generateLinkedInWordDocument(
          functionData.generatedContent,
          targetTitle
        );
        if (wordDoc) {
          setWordDocument(wordDoc);
          console.log('LinkedIn Word document generated successfully');
        }
      } catch (docError) {
        console.error('Failed to generate Word document:', docError);
        // Non-blocking - still show results even if doc generation fails
      }
      
      // Save the generated report only if we have a profileId (authenticated user)
      if (profileId) {
        try {
          const reportData = {
            profileStrength: 95,
            keywordOptimization: 92,
            contentQuality: 98,
            networkingPotential: 90,
            generationDate: new Date().toISOString(),
            implementationGuide: true,
            exportFormat: 'professional_report',
            aiGenerated: true
          };

          await LinkedInProfileService.saveGeneratedReport(profileId, result, reportData);
          
          // NOW deduct credits after successful save
          if (user) {
            const creditResult = await deductCredits('linkedin_profile_generation', { 
              profileId: profileId 
            });
            if (creditResult.success) {
              console.log('Credits deducted successfully:', creditResult.credits_used);
            } else {
              console.error('Credit deduction failed:', creditResult.error);
            }
          }
        } catch (saveError) {
          console.log('Could not save report (user may not be authenticated):', saveError);
        }
      }
      
      setCurrentStep(14); // Move to results
      
      toast.success("LinkedIn profile generated successfully with AI optimization!");
    } catch (error) {
      console.error('Error generating profile:', error);
      toast.error(`Failed to generate LinkedIn profile: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper functions to parse generated content
  const extractSection = (content: string, sectionTitle: string): string | null => {
    const regex = new RegExp(`\\*\\*${sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*([\\s\\S]*?)(?=\\*\\*|$)`, 'i');
    const match = content.match(regex);
    return match ? match[1].trim() : null;
  };

  const parseExperienceSection = (content: string): string[] => {
    const experienceSection = extractSection(content, 'EXPERIENCE SECTIONS:');
    if (!experienceSection) return [];
    
    return experienceSection.split('\n\n').filter(exp => exp.trim().length > 0);
  };

  const parseSkillsSection = (content: string): string[] => {
    const skillsSection = extractSection(content, 'SKILLS RECOMMENDATIONS:');
    if (!skillsSection) return [];
    
    return skillsSection.split(/[,\n]/).map(skill => skill.trim()).filter(skill => skill.length > 0);
  };

  const parseOptimizationTips = (content: string): string[] => {
    const tipsSection = extractSection(content, 'PROFILE OPTIMIZATION TIPS:');
    if (!tipsSection) return [];
    
    return tipsSection.split('\n').map(tip => tip.replace(/^[-•]\s*/, '').trim()).filter(tip => tip.length > 0);
  };
  const progress = (currentStep - 1) / (TOTAL_STEPS - 1) * 100;
  
  // Show professional report
  if (showProfessionalReport && generatedProfile && profileId) {
    return (
      <LinkedInProfessionalReport
        profileData={generatedProfile}
        rawContentData={wizardData}
        onBack={() => setShowProfessionalReport(false)}
      />
    );
  }

  // Show results
  if (currentStep === 14 && generatedProfile) {
    return (
      <LinkedInWizardResults 
        linkedinUrl={wizardData.linkedinUrl}
        photoUrl={wizardData.photoUrl}
        profile={generatedProfile}
        wordDocument={wordDocument}
        onBack={() => setCurrentStep(13)}
        onStartOver={() => {
          setGeneratedProfile(null);
          setWordDocument(null);
          setCurrentStep(1);
          setWizardData({
            userCategories: [],
            primaryGoals: [],
            uniqueSellingPoints: [],
            targetAudience: "",
            whatYouHelp: "",
            howYouHelp: "",
            valueProposition: "",
            industries: [],
            audienceSeniority: "",
            companySize: "",
            uploadedFiles: [],
            experienceHighlights: [],
            resultsAndProof: "",
            voiceStyle: "",
            includeFaith: "",
            keywords: "",
            targetJobTitles: "",
            pastJobTitles: "",
            skills: "",
            callToAction: ""
          });
          setRawContentSaved(false);
          setProfileId(null);
        }}
        onViewReport={() => setShowProfessionalReport(true)}
      />
    );
  }

  if (showResults) {
    return (
      <LinkedInWizardResults
        linkedinUrl={wizardData.linkedinUrl}
        photoUrl={wizardData.photoUrl}
        profile={wizardData}
        wordDocument={wordDocument}
        onBack={() => setShowResults(false)}
        onStartOver={() => {
          setShowResults(false);
          setWordDocument(null);
          setCurrentStep(1);
          setWizardData({
            userCategories: [],
            primaryGoals: [],
            uniqueSellingPoints: [],
            targetAudience: "",
            whatYouHelp: "",
            howYouHelp: "",
            valueProposition: "",
            industries: [],
            audienceSeniority: "",
            companySize: "",
            uploadedFiles: [],
            experienceHighlights: [],
            resultsAndProof: "",
            voiceStyle: "",
            includeFaith: "",
            keywords: "",
            targetJobTitles: "",
            pastJobTitles: "",
            skills: "",
            callToAction: ""
          });
        }}
      />
    );
  }
  const renderStep = () => {
    const stepProps = {
      data: wizardData,
      updateData: updateWizardData,
      onNext: handleNext,
      onPrev: prevStep
    };
    switch (currentStep) {
      case 1:
        return <LinkedInWizardStep1 {...stepProps} />;
      case 2:
        return <LinkedInWizardStep2 {...stepProps} />;
      case 3:
        return <LinkedInWizardStep3 {...stepProps} />;
      case 4:
        return <LinkedInWizardStep4 {...stepProps} />;
      case 5:
        return <LinkedInWizardStep5 {...stepProps} />;
      case 6:
        return <LinkedInWizardStep6 {...stepProps} />;
      case 7:
        return <LinkedInWizardStep7 {...stepProps} />;
      case 8:
        return <LinkedInWizardStep8 {...stepProps} />;
      case 9:
        return <LinkedInWizardStep9 {...stepProps} />;
      case 10:
        return <LinkedInWizardStep10 {...stepProps} />;
      case 11:
        return <LinkedInWizardStep11 data={wizardData} updateData={updateWizardData} onNext={nextStep} onPrev={prevStep} onGenerate={() => nextStep()} // Go to recap step instead of generating directly
        />;
      case 12:
        return <LinkedInWizardRecap data={wizardData} onEdit={goToStep} onGenerate={() => nextStep()} onBack={prevStep} />;
      case 13:
        return (
          <LinkedInWizardContentReview 
            data={wizardData}
            onNext={handleContentReviewNext}
            onBack={() => setCurrentStep(12)}
            onSaveRawContent={handleSaveRawContent}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <UpgradePrompt
          feature="LinkedIn Profile Generation"
          creditsNeeded={LINKEDIN_WIZARD_CREDIT_COST}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      )}
      
    <div className="min-h-screen bg-background w-full">
      {/* Header with back button and powered by TailorWiz */}
      <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm backdrop-blur-sm px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">LinkedIn Profile Optimization</h1>
            </div>
          </div>
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm px-4 py-2">
            <span className="text-xs text-muted-foreground font-medium tracking-wide">powered by </span>
            <span className="text-xs font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent tracking-wide">TailorWiz</span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-background via-muted/30 to-accent/15 min-h-screen">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Hero Section with LinkedIn colors */}
          <div className="text-center space-y-6 mb-8">
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="bg-[#0077B5] p-4 rounded-2xl shadow-lg">
                  <img 
                    src="https://content.linkedin.com/content/dam/me/business/en-us/amp/brand-site/v2/bg/LI-Logo.svg.original.svg" 
                    alt="LinkedIn" 
                    className="h-10 w-auto filter brightness-0 invert"
                  />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight">
                <span className="bg-gradient-to-r from-[#0077B5] via-[#0E76A8] to-[#004471] bg-clip-text text-transparent drop-shadow-sm">
                  LinkedIn Profile Optimization Wizard
                </span>
              </h1>
              <p className="text-muted-foreground text-xl font-bold">
                Step {currentStep} of {TOTAL_STEPS}: {stepTitles[currentStep - 1]}
              </p>
            </div>
          </div>

          {/* Enhanced Progress Card */}
          <Card className="mb-6 shadow-xl border-2 border-[#0077B5]/20">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg text-[#0077B5] font-bold">LinkedIn Profile Creation Progress</h2>
                <span className="text-lg text-[#0077B5] font-bold">
                  Step {currentStep} of {TOTAL_STEPS}
                </span>
              </div>
            
            {/* Visual Progress Steps */}
            <div className="flex items-center justify-between mb-4 overflow-x-auto pb-2">
              {stepShortNames.map((stepName, index) => {
              const stepNumber = index + 1;
              const isCompleted = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              const StepIcon = stepIcons[index];
              return <div key={stepNumber} className="flex flex-col items-center min-w-0 flex-1 relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${isCompleted ? 'bg-[#0077B5] text-white shadow-lg' : isCurrent ? 'bg-[#0077B5]/90 text-white shadow-lg' : 'bg-muted text-muted-foreground'}`}>
                      {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs font-medium text-center truncate max-w-full transition-colors duration-300 ${isCompleted ? 'text-[#0077B5]' : isCurrent ? 'text-[#0077B5]' : 'text-muted-foreground'}`}>
                      {stepName}
                    </span>
                    
                    {/* Connection Line */}
                    {index < stepShortNames.length - 1 && <div className={`absolute top-5 left-1/2 w-full h-0.5 transition-colors duration-300 ${stepNumber < currentStep ? 'bg-[#0077B5]/60' : 'bg-muted'}`} style={{
                  transform: 'translateX(20px)',
                  width: 'calc(100% - 40px)',
                  zIndex: -1
                }} />}
                  </div>;
            })}
            </div>
            
            {/* Traditional Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{stepTitles[currentStep - 1]}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2 [&>div]:!bg-success [&>div]:!bg-none" style={{
              "--gradient-primary": "none"
            } as React.CSSProperties} />
            </div>
            </div>
          </Card>

          {/* Step Content */}
          <Card className="mb-6 shadow-xl border-2 border-[#0077B5]/20">
            <div className="p-8">
              {renderStep()}
            </div>
          </Card>

          {/* Navigation - Only show for steps 1-11, step 12 (recap) has its own navigation */}
          {currentStep < 12 && (
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={prevStep} 
                disabled={currentStep === 1} 
                className="px-6 border-[#0077B5]/30 text-[#0077B5] hover:bg-[#0077B5]/10"
              >
                ← Previous
              </Button>
              <Button 
                onClick={currentStep === 11 ? nextStep : handleNext} 
                className="px-6 bg-[#0077B5] hover:bg-[#005885] text-white shadow-lg"
              >
                {currentStep === 11 ? "Review Profile →" : "Next →"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}